import { Button, Checkbox, Input, LoadSpinner, Select } from '@calizahq/react-components-v2'
import { Form, Formik } from 'formik'
import React, { useMemo, useState } from 'react'

import { TimeInputControlled } from '@/components'
import { ScheduledCommunicationTypeMutations } from '@/graphql/mutations/scheduledCommunicationType.mutations'
import { useMobile } from '@/hooks'
import { useGraphqlMutation } from '@/hooks/useGraphql'
import useScheduledCommunicationType from '@/hooks/useScheduledCommunicationTypes/useScheduledCommunicationType'
import {
  ScheduledCommunicationTypeBaselineMetricTypes,
  ScheduledCommunicationTypeScheduleUnits,
} from '@/types/ScheduledCommunicationType'
import { formatDate } from '@/util'

import { formSchema, FormValuesType } from './EditScheduledCommunicationTypeForm.util'

type Props = {
  refetchOnSuccess?: () => void
  scheduledCommunicationTypeId: number
  toggle: () => void
}

export const EditScheduledCommunicationTypeForm = ({
  toggle,
  refetchOnSuccess,
  scheduledCommunicationTypeId,
}: Props) => {
  const { isLoading, data } = useScheduledCommunicationType(scheduledCommunicationTypeId)

  const timeToSend = useMemo(() => {
    return data?.scheduledCommunicationType.timeToSend
      ? new Date(`2000-01-01T${data?.scheduledCommunicationType.timeToSend}`)
      : undefined
  }, [data])

  const [error, setError] = useState<string>()

  const { isTablet } = useMobile()

  const scheduledCommunicationTypeScheduleUnitOptions = Object.values(ScheduledCommunicationTypeScheduleUnits).map(
    (scheduledCommunicationTypeScheduleUnit) => {
      return { label: scheduledCommunicationTypeScheduleUnit, value: scheduledCommunicationTypeScheduleUnit }
    },
  )

  const scheduledCommunicationTypeBaselineMetricOptions = Object.values(
    ScheduledCommunicationTypeBaselineMetricTypes,
  ).map((scheduledCommunicationTypeBaselineMetric) => {
    return { label: scheduledCommunicationTypeBaselineMetric, value: scheduledCommunicationTypeBaselineMetric }
  })

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(
    ScheduledCommunicationTypeMutations.updateScheduledCommunicationType(),
    {
      onSuccess: () => {
        refetchOnSuccess && refetchOnSuccess()
        toggle && toggle()
      },
      onError: (res: { response: { errors: { message: string }[] } }) => {
        const { response } = res
        if (response.errors && response.errors.length) {
          setError(response.errors[0].message)
        }
      },
    },
  )

  const handleSubmit = (values: FormValuesType) => {
    if (data?.scheduledCommunicationType) {
      const { timeToSend, ...restOfValues } = values
      const formattedTimeToSend = timeToSend ? formatDate(timeToSend, 'kk:mm:ss') : null
      mutate({
        scheduledCommunicationType: {
          scheduledCommunicationTypeId: data.scheduledCommunicationType.id,
          timeToSend: formattedTimeToSend,
          ...restOfValues,
        },
      })
    }
  }

  return (
    <>
      {isLoading ? (
        <div className="flex h-120 w-full items-center justify-center">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <>
          <Formik
            enableReinitialize={true}
            initialValues={{
              active: data?.scheduledCommunicationType && data.scheduledCommunicationType.active,
              leadTime: data?.scheduledCommunicationType && data.scheduledCommunicationType.leadTime,
              numberOfScheduleUnits:
                data?.scheduledCommunicationType && data.scheduledCommunicationType.numberOfScheduleUnits,
              scheduleUnits: data?.scheduledCommunicationType && data.scheduledCommunicationType.scheduleUnits,
              baselineMetric: data?.scheduledCommunicationType && data.scheduledCommunicationType.baselineMetric,
              timeToSend: timeToSend ? timeToSend : '',
              defaultToApproved: data?.scheduledCommunicationType && data.scheduledCommunicationType.defaultToApproved,
            }}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validationSchema={formSchema}
          >
            {() => {
              return (
                <Form className="flex min-h-140 w-full flex-col flex-wrap px-6 md:max-w-160">
                  <p className="leading-nonemt-4 mb-4 font-bold text-header-md">Edit Scheduled Communication Type</p>

                  <Checkbox className="mb-4 w-full" id="active" label="Active?" name="active" />
                  <Input
                    className="mb-4 w-full"
                    id="leadTime"
                    label="Lead Time (in days)"
                    min={0}
                    name="leadTime"
                    type="number"
                  />
                  <div className="flex w-full mb-4 grid-cols-3 flex-row md:gap-4">
                    <Input
                      className="w-1/4"
                      id="numberOfScheduleUnits"
                      label="Number of units"
                      name="numberOfScheduleUnits"
                      type="number"
                    />
                    <Select
                      className="mb-4 w-1/4"
                      id="scheduleUnits"
                      label="Schedule units"
                      name="scheduleUnits"
                      options={scheduledCommunicationTypeScheduleUnitOptions}
                    ></Select>
                    <Select
                      className="mb-4 w-1/2"
                      id="baselineMetric"
                      label="Baseline metric"
                      name="baselineMetric"
                      options={scheduledCommunicationTypeBaselineMetricOptions}
                    ></Select>
                  </div>
                  <TimeInputControlled
                    className="mb-4 w-full"
                    endHour={18}
                    label="Time to Send (local to the Reservation)"
                    name="timeToSend"
                    startHour={8}
                    timeFormat="HH:mm:ss"
                    timeIntervals={30}
                  />
                  <Checkbox
                    className="mb-4 w-full"
                    id="defaultToApproved"
                    label="Default to approved?"
                    name="defaultToApproved"
                  />

                  {error && <p className=" mb-4 text-p-sm text-text-semantic-error">{error}</p>}
                  <div className="flex flex-col-reverse justify-between md:flex-row">
                    <Button
                      className="w-full bg-action-secondary md:flex md:w-auto"
                      onClick={toggle}
                      size={isTablet ? 'medium' : 'small'}
                      variant="secondary"
                    >
                      Close
                    </Button>
                    <Button
                      className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
                      disabled={isMutationLoading}
                      isLoading={isMutationLoading}
                      size={isTablet ? 'medium' : 'small'}
                      type="submit"
                    >
                      Update
                    </Button>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </>
      )}
    </>
  )
}
